import React, { useState } from 'react';
import { X } from 'lucide-react';
import { WorkflowDefinition } from '../types';
import { workflowsService } from '../workflowsService';
import { useEscapeKey } from '../../../shared/useGlobalModalEscape';
import { useDialog } from '../../../shared/DialogContext';

interface Props {
  definitions: WorkflowDefinition[];
  onClose: () => void;
}

export const WorkflowSimulationModal: React.FC<Props> = ({ definitions, onClose }) => {
  useEscapeKey(onClose);
  const { showError } = useDialog();

  const [selectedDefinitionId, setSelectedDefinitionId] = useState(definitions[0]?.id || '');
  const [severity, setSeverity] = useState('HIGH');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ path: Array<{ nodeId: string; nodeName: string; nodeType: string }>; finalOutcome: string } | null>(null);

  const handleSimulate = async () => {
    if (!selectedDefinitionId) return;
    setLoading(true);
    try {
      const res = await workflowsService.simulateWorkflow(selectedDefinitionId, { severity });
      setResult(res);
    } catch (err: any) {
      showError(err, 'Workflow Simulation Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>🧪 Workflow Simulation Engine</h3>
            <p>Sandbox simulation of transition rules and decision path evaluation</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body login-form">
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
            Test condition transitions and decision paths in a sandbox environment before publication or trigger.
          </p>

          <div className="form-field">
            <label>Workflow Definition</label>
            <select
              value={selectedDefinitionId}
              onChange={(e) => setSelectedDefinitionId(e.target.value)}
            >
              {definitions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.code})
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>Simulated Parameter: Severity</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
            >
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>

          <button
            onClick={handleSimulate}
            disabled={loading}
            className="btn-primary"
            style={{ width: 'auto', alignSelf: 'flex-start', background: '#7c3aed' }}
          >
            {loading ? 'Simulating...' : 'Execute Simulation'}
          </button>

          {result && (
            <div style={{ marginTop: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#0f172a' }}>Simulation Execution Trace:</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                {result.path.map((step, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                    <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                      Step {idx + 1}
                    </span>
                    <span style={{ fontWeight: 600, color: '#334155' }}>{step.nodeName}</span>
                    <span style={{ color: '#94a3b8' }}>({step.nodeType})</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '13px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                Outcome: <strong style={{ color: '#059669' }}>{result.finalOutcome}</strong>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
export default WorkflowSimulationModal;
